/**
 * @file lastLoginStore.ts
 *
 * @description Persisted record of the admin's own sign-in timestamps, used to
 * bound the Station Admin "since you last logged in" activity window.
 *
 * WorkOS exposes `user.lastSignInAt`, but immediately after a login that value
 * is the CURRENT session's sign-in (≈ now) — using it directly would yield an
 * empty window. So this store keeps two values and shifts them on each new
 * authentication: when a later sign-in timestamp arrives, the previously
 * recorded "current" becomes "previous" (the window start), and the new one
 * becomes "current". Older responses are ignored so asynchronous auth updates
 * cannot move the window backward.
 *
 * The record is keyed by WorkOS user id and DELIBERATELY survives logout: an
 * admin who signs out Friday and returns Monday should get "since Friday", not
 * a fallback window. When a DIFFERENT user signs in on the same browser, the
 * mismatch on userId resets the record, so one admin's login times never bound
 * another admin's window (the data itself is always org-scoped server-side).
 *
 * @module Store
 * @requires zustand
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

function parseSignInTimestamp(value: string | null): number | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

interface LastLoginState {
  /** WorkOS user id the recorded timestamps belong to. */
  userId: string | null;
  /** The sign-in timestamp recorded as the admin's current session. */
  currentSignInAt: string | null;
  /** The sign-in timestamp of the admin's previous session — the window
   *  start. Null until a second distinct login has been observed. */
  previousSignInAt: string | null;
  /**
   * Records the freshest `user.lastSignInAt` seen from WorkOS for this user.
   * A different userId resets the record (new admin on this browser). For the
   * same user, current→previous shifts only when a later valid timestamp
   * arrives (a real re-authentication), so a plain reload, token refresh, or
   * stale response never collapses or regresses the window.
   */
  recordSignIn: (userId: string | null, lastSignInAt: string | null) => void;
  /** Test-reset helper. Deliberately NOT wired into sign-out — the record
   *  must survive logout so a returning admin gets a real window (see the
   *  module doc). */
  clear: () => void;
}

export const useLastLoginStore = create<LastLoginState>()(
  persist(
    (set, get) => ({
      userId: null,
      currentSignInAt: null,
      previousSignInAt: null,

      recordSignIn: (userId, lastSignInAt) => {
        if (!userId) return;
        const incomingTimestamp = parseSignInTimestamp(lastSignInAt);
        if (incomingTimestamp === null) return;

        const state = get();
        if (state.userId !== userId) {
          // Different admin on this browser — start their record fresh so the
          // prior user's login times never define this user's window.
          set({
            userId,
            currentSignInAt: lastSignInAt,
            previousSignInAt: null,
          });
          return;
        }
        if (state.currentSignInAt === null) {
          set({ currentSignInAt: lastSignInAt, previousSignInAt: null });
          return;
        }

        const currentTimestamp = parseSignInTimestamp(state.currentSignInAt);
        if (currentTimestamp === null) {
          // Repair malformed persisted state without promoting its stale
          // previous boundary into the new activity window.
          set({ currentSignInAt: lastSignInAt, previousSignInAt: null });
          return;
        }

        if (incomingTimestamp <= currentTimestamp) return;

        // A later authentication since we last recorded — the old "current"
        // is now the previous session boundary.
        set({
          previousSignInAt: state.currentSignInAt,
          currentSignInAt: lastSignInAt,
        });
      },

      clear: () =>
        set({ userId: null, currentSignInAt: null, previousSignInAt: null }),
    }),
    {
      name: "station-admin-last-login",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        userId: state.userId,
        currentSignInAt: state.currentSignInAt,
        previousSignInAt: state.previousSignInAt,
      }),
    },
  ),
);
